import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import axios from "axios";
import { Gift, Percent, Sparkles, CalendarClock, ArrowRight } from "lucide-react";
import { API } from "@/config";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PromoBanner } from "@/components/layout/PromoBanner";

const kindBadge = (kind) => (
  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
    kind === "free_item"
      ? "border-[#FFD166]/60 text-[#FFD166] bg-[#FFD166]/10"
      : "border-emerald-400/50 text-emerald-300 bg-emerald-500/10"
  }`}>
    {kind === "free_item" ? <><Gift className="w-3 h-3" /> Free Gift</> : <><Percent className="w-3 h-3" /> % Off</>}
  </span>
);

const formatDate = (iso) => {
  if (!iso) return null;
  const s = iso.length > 10 ? iso.slice(0, 10) : iso;
  try {
    return new Date(s + "T00:00:00Z").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
  } catch {
    return s;
  }
};

export const Deals = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/promos/active`).then(({ data }) => {
      if (!alive) return;
      setPromos(data.promos || []);
    }).catch(() => {}).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#0F1E2C] text-[#F6F1E4]">
      <Helmet>
        <title>Deals & Promotions · Enchanted Gear Rentals</title>
        <meta name="description" content="Limited-time promotions on baby gear rentals near Walt Disney World — free accessories, percent-off bundles, and seasonal offers." />
      </Helmet>
      <PromoBanner />
      <Nav />
      <main className="pt-32 pb-24">
        <div className="container">
          <div className="max-w-3xl mb-12">
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#FFD166] mb-3">Current Offers</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-4">
              Enchanted <span style={{ background: "linear-gradient(135deg, #F4A93A 0%, #FFD166 50%, #D97B37 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Deals</span>
            </h1>
            <p className="text-[#B9C8D5] text-base sm:text-lg">
              Automatic promotions applied at checkout — no codes to remember. Add the qualifying items to your cart and the discount lands on its own.
            </p>
          </div>

          {loading ? (
            <div className="text-[#B9C8D5] text-center py-20">Loading offers…</div>
          ) : promos.length === 0 ? (
            <div className="border border-dashed border-[#FFD166]/25 rounded-2xl p-12 text-center" data-testid="deals-empty">
              <Sparkles className="w-8 h-8 text-[#FFD166] mx-auto mb-3 opacity-70" />
              <div className="font-serif text-2xl mb-2">No active promotions right now</div>
              <p className="text-[#B9C8D5] text-sm">
                Check back soon — we run seasonal offers throughout the year.
              </p>
              <Link to="/#catalog" className="inline-flex items-center gap-1 text-[#FFD166] text-sm mt-4 hover:underline">
                Browse the catalog <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2" data-testid="deals-grid">
              {promos.map((p) => (
                <div key={p.id} className="relative rounded-2xl border border-[#FFD166]/30 bg-gradient-to-br from-[#FFD166]/5 via-transparent to-transparent p-6 sm:p-8 backdrop-blur-sm overflow-hidden" data-testid={`deal-card-${p.id}`}>
                  <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-30" style={{ background: p.kind === "free_item" ? "radial-gradient(circle, #FFD166 0%, transparent 65%)" : "radial-gradient(circle, #10b981 0%, transparent 65%)" }} />
                  <div className="relative">
                    <div className="mb-3">{kindBadge(p.kind)}</div>
                    <h2 className="font-serif text-2xl sm:text-3xl leading-tight mb-3">{p.banner_headline}</h2>
                    {p.subtitle && <p className="text-[#B9C8D5] text-sm mb-4">{p.subtitle}</p>}

                    <div className="text-xs text-[#B9C8D5] space-y-1 mb-6">
                      {Number(p.min_spend) > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-[#FFD166]">·</span>
                          Minimum spend: <b className="text-[#F6F1E4]">${Number(p.min_spend).toFixed(0)}</b>
                          {(p.required_product_ids || []).length > 0 && <span className="text-[#B9C8D5]/70">(on qualifying items)</span>}
                        </div>
                      )}
                      {p.ends_at && (
                        <div className="flex items-center gap-2">
                          <CalendarClock className="w-3.5 h-3.5 text-[#FFD166]" />
                          Ends {formatDate(p.ends_at)}
                        </div>
                      )}
                      {p.starts_at && new Date().toISOString() < p.starts_at && (
                        <div className="flex items-center gap-2 text-sky-300">
                          <CalendarClock className="w-3.5 h-3.5" />
                          Starts {formatDate(p.starts_at)}
                        </div>
                      )}
                    </div>

                    <Link to="/#catalog" className="inline-flex items-center gap-2 text-sm text-[#FFD166] hover:underline" data-testid={`deal-shop-${p.id}`}>
                      Shop the catalog <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-12 border-t border-[#FFD166]/15 pt-8 text-xs text-[#B9C8D5]/80 max-w-2xl">
            All promotions apply automatically at checkout — no code required. Only one promotion is applied per booking (we always pick the best-value one for you). Excludes taxes and delivery fees. Offers subject to change.
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Deals;
